/*
 * rheincool — Produktberater (Kontakt-Produktberater-Handoff Teil 2)
 * Drei Fragen (Raumgröße → Räume → Budget), Fortschrittskette,
 * Preisanker live aus dem eingebetteten Katalog, Zustand in der URL +
 * sessionStorage, Beratungs-Ausstieg auf jedem Schritt, Ergebnis mit
 * Begründung und drei Empfehlungen (erste hervorgehoben, oranger CTA).
 * Regeln aus dem advisor_rule-Metaobjekt; Null-Treffer → nächstliegende
 * Empfehlung + Beratungs-CTA. Kein Orange im Frage-Flow.
 */
class RcProductAdvisor extends HTMLElement {
  connectedCallback() {
    const read = (sel) => {
      try {
        return JSON.parse(this.querySelector(sel).textContent);
      } catch {
        return null;
      }
    };
    this.produkte = read('[data-advisor-produkte]') || [];
    this.stufen = read('[data-advisor-stufen]') || [];
    this.regeln = read('[data-advisor-regeln]') || [];
    this.s = read('[data-advisor-strings]') || {};

    this.stage = this.querySelector('[data-advisor-stage]');
    this.standing = this.querySelector('[data-advisor-standing]');
    this.chipsEl = this.querySelector('[data-advisor-chips]');
    this.remainingEl = this.querySelector('[data-advisor-remaining]');
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // m²-Bänder der Antwortkarten: klein / mittel / groß
    this.roomBands = [
      { key: 's', min: 10, max: 25 },
      { key: 'm', min: 25, max: 45 },
      { key: 'l', min: 45, max: 90 },
    ];

    this.state = this.restore();
    this.step = this.firstOpenStep();
    this.track('advisor_start', { einstieg: document.referrer ? 'link' : 'direkt' });
    this.render(false);

    window.addEventListener('popstate', () => {
      this.state = this.fromUrl();
      this.step = this.firstOpenStep();
      this.render(false, true);
    });
  }

  track(event, params) {
    if (window.rcTrack) window.rcTrack(event, params || {});
  }

  /* ---------- Zustand: URL → sessionStorage → leer ---------- */

  fromUrl() {
    const p = new URLSearchParams(location.search);
    return {
      raum: p.get('raum') || null,
      anzahl: p.get('anzahl') || null,
      budget: p.get('budget') || null,
    };
  }

  restore() {
    const url = this.fromUrl();
    if (url.raum || url.anzahl || url.budget) return url;
    try {
      const saved = JSON.parse(sessionStorage.getItem('rc-advisor'));
      if (saved) return saved;
    } catch {
      /* leer */
    }
    return { raum: null, anzahl: null, budget: null };
  }

  persist(push) {
    sessionStorage.setItem('rc-advisor', JSON.stringify(this.state));
    const p = new URLSearchParams();
    Object.entries(this.state).forEach(([k, v]) => {
      if (v) p.set(k, v);
    });
    const qs = p.toString();
    const target = location.pathname + (qs ? `?${qs}` : '');
    if (push) history.pushState(null, '', target);
    else history.replaceState(null, '', target);
  }

  firstOpenStep() {
    if (!this.state.raum) return 1;
    if (!this.state.anzahl) return 2;
    if (!this.state.budget) return 3;
    return 4; // Ergebnis
  }

  /* ---------- Preisanker aus dem Katalog ---------- */

  kwForM2(m2) {
    for (const stufe of this.stufen) {
      if (m2 <= stufe.m2) return stufe.kw;
    }
    return this.stufen.length ? this.stufen[this.stufen.length - 1].kw : 5;
  }

  candidatesFor(band) {
    const kwMin = this.kwForM2(band.min) - 0.6;
    const kwMax = this.kwForM2(band.max) + 0.6;
    return this.produkte.filter((p) => p.kw >= kwMin && p.kw <= kwMax);
  }

  minPrice(list) {
    let min = null;
    list.forEach((p) => {
      if (p.p > 150 && (min === null || p.p < min)) min = p.p;
    });
    return min;
  }

  money(value) {
    return `${Math.round(value).toLocaleString('de-DE')},– €`;
  }

  esc(str) {
    const div = document.createElement('div');
    div.textContent = String(str == null ? '' : str);
    return div.innerHTML;
  }

  /* ---------- Frage-Screens ---------- */

  card(value, index, title, hint, anchor) {
    return (
      `<button type="button" class="rc-advisor__card" role="radio" aria-checked="false" data-answer="${this.esc(value)}">` +
      `<span class="rc-advisor__card-num rc-num" aria-hidden="true">${index}</span>` +
      `<span class="rc-advisor__card-radio" aria-hidden="true"></span>` +
      `<span class="rc-advisor__card-title">${this.esc(title)}</span>` +
      (hint ? `<span class="rc-advisor__card-hint">${this.esc(hint)}</span>` : '') +
      (anchor ? `<span class="rc-advisor__card-anchor rc-num">${this.esc(anchor)}</span>` : '') +
      `</button>`
    );
  }

  question(step) {
    const s = this.s;
    let title = '';
    let hint = '';
    let cards = '';
    if (step === 1) {
      title = s.qRoom;
      hint = s.qRoomHint;
      cards = this.roomBands
        .map((band, i) => {
          const min = this.minPrice(this.candidatesFor(band));
          return this.card(band.key, i + 1, s.answers.roomSizes[i], s.answers.roomHints[i], min ? `${s.from} ${this.money(min)}` : '');
        })
        .join('');
    } else if (step === 2) {
      title = s.qRooms;
      hint = s.qRoomsHint;
      cards = ['1', '2', '3', '4plus'].map((v, i) => this.card(v, i + 1, s.answers.rooms[i], '', '')).join('');
    } else {
      title = s.qBudget;
      hint = s.qBudgetHint;
      cards = ['bis-1500', 'bis-2500', 'offen'].map((v, i) => this.card(v, i + 1, s.answers.budget[i], '', '')).join('');
    }

    const nextLabel = step === 3 ? s.showResult : s.nextTo.replace('[n]', step + 1);
    return (
      `<section class="rc-advisor__screen" data-screen>` +
      `<p class="rc-advisor__overline">${this.esc(s.overline.replace('[n]', step))}</p>` +
      `<h1 class="rc-advisor__question rc-heading" tabindex="-1" data-question>${this.esc(title)}</h1>` +
      `<p class="rc-advisor__hint">${this.esc(hint)}</p>` +
      `<div class="rc-advisor__cards" role="radiogroup" aria-label="${this.esc(title)}" data-cards>${cards}</div>` +
      `<div class="rc-advisor__nav">` +
      (step > 1 ? `<button type="button" class="rc-advisor__back" data-back>← ${this.esc(s.back)}</button>` : '<span></span>') +
      `<button type="button" class="rc-advisor__next" data-next disabled>${this.esc(nextLabel)}</button>` +
      `</div></section>`
    );
  }

  stateKey(step) {
    return ['raum', 'anzahl', 'budget'][step - 1];
  }

  render(push, fromHistory) {
    if (this.step >= 4) {
      this.renderResult(fromHistory);
      return;
    }
    this.stage.innerHTML = this.question(this.step);
    this.updateChain(this.step);
    this.renderStanding();

    const key = this.stateKey(this.step);
    const cards = Array.from(this.stage.querySelectorAll('[data-answer]'));
    const next = this.stage.querySelector('[data-next]');
    const select = (card) => {
      cards.forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this.state[key] = card.dataset.answer;
      next.disabled = false;
    };
    cards.forEach((card, idx) => {
      if (card.dataset.answer === this.state[key]) select(card);
      card.addEventListener('click', () => select(card));
      card.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          (cards[idx + 1] || cards[0]).focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          (cards[idx - 1] || cards[cards.length - 1]).focus();
        }
      });
    });
    next.addEventListener('click', () => {
      this.track('advisor_step', { frage: this.step, antwort: this.state[key] });
      this.step += 1;
      this.persist(true);
      this.render(false);
    });
    const back = this.stage.querySelector('[data-back]');
    if (back) {
      back.addEventListener('click', () => {
        this.track('advisor_back', { frage: this.step });
        this.step -= 1;
        this.render(false);
      });
    }

    if (!fromHistory) this.persist(false);
    if (this.firstRenderDone) this.stage.querySelector('[data-question]').focus();
    this.firstRenderDone = true;
    this.animateIn();
  }

  animateIn() {
    if (this.reduced) return;
    const screen = this.stage.querySelector('[data-screen], [data-result]');
    if (!screen) return;
    screen.style.opacity = '0';
    screen.style.transform = 'translateX(16px)';
    requestAnimationFrame(() => {
      screen.style.transition = 'opacity 250ms cubic-bezier(.2,.6,.2,1), transform 250ms cubic-bezier(.2,.6,.2,1)';
      screen.style.opacity = '1';
      screen.style.transform = 'none';
    });
  }

  updateChain(active) {
    this.querySelectorAll('[data-chain-step]').forEach((el) => {
      const n = parseInt(el.dataset.chainStep, 10);
      el.classList.toggle('is-done', n < active);
      el.classList.toggle('is-active', n === active);
      if (n === Math.min(active, 3)) el.setAttribute('aria-current', 'step');
      else el.removeAttribute('aria-current');
    });
    this.querySelectorAll('[data-chain-line]').forEach((el) => {
      el.classList.toggle('is-done', parseInt(el.dataset.chainLine, 10) < active);
    });
    const bar = this.querySelector('[data-advisor-bar]');
    if (bar) {
      bar.setAttribute('aria-valuenow', Math.min(active, 4));
      this.querySelector('[data-advisor-bar-fill]').style.width = `${(Math.min(active, 4) / 4) * 100}%`;
    }
  }

  answerLabel(key) {
    const s = this.s.answers;
    const v = this.state[key];
    if (!v) return null;
    if (key === 'raum') return s.roomSizes[{ s: 0, m: 1, l: 2 }[v]];
    if (key === 'anzahl') return s.rooms[{ 1: 0, 2: 1, 3: 2, '4plus': 3 }[v]];
    return s.budget[{ 'bis-1500': 0, 'bis-2500': 1, offen: 2 }[v]];
  }

  renderStanding() {
    const keys = ['raum', 'anzahl', 'budget'];
    const answered = keys.filter((k) => this.state[k] && this.stateKey(this.step) !== k);
    this.standing.hidden = answered.length === 0;
    this.chipsEl.innerHTML = answered
      .map((k) => `<button type="button" class="rc-advisor__chip" data-goto="${keys.indexOf(k) + 1}">${this.esc(this.answerLabel(k))} · <u>${this.esc(this.s.change)}</u></button>`)
      .join('');
    this.chipsEl.querySelectorAll('[data-goto]').forEach((chip) => {
      chip.addEventListener('click', () => {
        this.step = parseInt(chip.dataset.goto, 10);
        this.render(false);
      });
    });
    const remaining = keys.length - keys.filter((k) => this.state[k]).length;
    this.remainingEl.textContent = remaining > 0 ? this.s.remaining.replace('[n]', remaining) : '';
  }

  /* ---------- Ergebnis ---------- */

  budgetLimit() {
    if (this.state.budget === 'bis-1500') return 1500;
    if (this.state.budget === 'bis-2500') return 2500;
    return null;
  }

  matchRule() {
    const band = this.roomBands.find((b) => b.key === this.state.raum) || this.roomBands[1];
    const mid = (band.min + band.max) / 2;
    return (
      this.regeln.find((r) => {
        if (mid < r.min || mid > r.max) return false;
        if (r.budget !== 'egal' && r.budget !== this.state.budget) return false;
        return true;
      }) || null
    );
  }

  totalPrice(p) {
    return p.p;
  }

  renderResult(fromHistory) {
    const s = this.s;
    this.updateChain(4);
    const band = this.roomBands.find((b) => b.key === this.state.raum) || this.roomBands[1];
    let list = this.candidatesFor(band);

    const limit = this.budgetLimit();
    let relaxed = false;
    if (limit) {
      const within = list.filter((p) => this.totalPrice(p) <= limit);
      if (within.length >= 1) list = within;
      else relaxed = true; // Null-Treffer: nächstliegende Empfehlungen zeigen
    }

    const rule = this.matchRule();
    const sortKey = rule ? rule.sort : 'preis';
    const sorted = list.slice().sort((a, b) => {
      if (sortKey === 'leise') return (a.db || 99) - (b.db || 99) || a.p - b.p;
      if (sortKey === 'effizienz') return (b.seer || 0) - (a.seer || 0) || a.p - b.p;
      return this.totalPrice(a) - this.totalPrice(b);
    });

    let top = sorted.slice(0, 3);
    if (rule && rule.produkte) {
      const pinned = rule.produkte
        .map((u) => this.produkte.find((p) => p.u.split('?')[0] === u.split('?')[0]))
        .filter(Boolean);
      if (pinned.length) top = pinned.concat(top.filter((p) => !pinned.includes(p))).slice(0, 3);
    }

    const multi = this.state.anzahl && this.state.anzahl !== '1';
    const chips = ['raum', 'anzahl', 'budget']
      .map((k) => `<span class="rc-advisor__result-chip">${this.esc(this.answerLabel(k))}</span>`)
      .join('');

    const reasonFor = (p, i) => {
      if (sortKey === 'leise' && p.db) return s.reasonQuiet.replace('[db]', p.db);
      if (sortKey === 'effizienz' && p.seer) return s.reasonSeer.replace('[seer]', p.seer);
      return s.reasonPrice;
    };

    const cards = top
      .map((p, i) => {
        const total = this.totalPrice(p);
        const priceNote = s.deviceOnly;
        const cta = p.u;
        return (
          `<article class="rc-advisor__reco${i === 0 ? ' rc-advisor__reco--best' : ''}"${this.reduced ? '' : ` style="animation-delay:${i * 80}ms"`}>` +
          (i === 0 ? `<span class="rc-advisor__reco-badge">${this.esc(s.bestChoice)}</span>` : '') +
          `<span class="rc-advisor__reco-media">${p.img ? `<img src="${this.esc(p.img)}" alt="" loading="lazy">` : ''}</span>` +
          `<span class="rc-advisor__reco-brand">${this.esc(p.marke)}</span>` +
          `<a class="rc-advisor__reco-title" href="${this.esc(cta)}" data-rc-track="advisor_result_cta">${this.esc(p.t)}</a>` +
          `<span class="rc-advisor__reco-chips">` +
          `<span class="rc-num">${p.kw} kW</span>` +
          (p.db ? `<span class="rc-num">${p.db} dB(A)</span>` : '') +
          (p.m2 ? `<span class="rc-num">bis ${p.m2} m²</span>` : '') +
          (p.energie ? `<span class="rc-advisor__reco-energy">${this.esc(p.energie)}</span>` : '') +
          `</span>` +
          `<span class="rc-advisor__reco-reason">${this.esc(reasonFor(p, i))}</span>` +
          `<span class="rc-advisor__reco-price"><b class="rc-num">${this.money(total)}</b><span>${this.esc(priceNote)}</span></span>` +
          `<a class="rc-advisor__reco-cta${i === 0 ? ' is-primary' : ''}" href="${this.esc(cta)}" data-rc-track="advisor_result_cta">${this.esc(i === 0 ? s.ctaFirst : s.ctaOther)}</a>` +
          `</article>`
        );
      })
      .join('');

    const wa = s.waNumber
      ? `<a class="rc-advisor__wa" href="https://wa.me/${this.esc(s.waNumber)}?text=${encodeURIComponent(s.whatsappCheck)}" target="_blank" rel="noopener" data-rc-track="advisor_whatsapp_check">${this.esc(s.whatsappCheck)}</a>`
      : '';

    this.stage.innerHTML =
      `<section class="rc-advisor__resultwrap" data-result>` +
      `<div class="rc-advisor__reveal">` +
      `<span class="rc-advisor__result-badge rc-num">${this.esc(s.resultBadge)}</span>` +
      `<h1 class="rc-advisor__result-title rc-heading" tabindex="-1" data-question>${this.esc(s.resultTitle.replace('[antwort]', this.answerLabel('raum')))}</h1>` +
      `<div class="rc-advisor__result-chips">${chips}<button type="button" class="rc-advisor__chip rc-advisor__chip--light" data-restart><u>${this.esc(s.changeAnswers)}</u></button></div>` +
      `</div>` +
      (rule && rule.begruendung ? `<div class="rc-advisor__why"><p>${this.esc(rule.begruendung)}</p></div>` : '') +
      (relaxed ? `<p class="rc-advisor__nohit">${this.esc(s.noHit)}</p>` : '') +
      (multi ? `<div class="rc-advisor__multisplit"><p>${this.esc(s.multisplitHint)}</p><a class="rc-advisor__multisplit-cta" href="${this.esc(s.multisplitUrl)}">${this.esc(s.multisplitCta)} →</a></div>` : '') +
      `<div class="rc-advisor__recos">${cards}</div>` +
      `<div class="rc-advisor__result-foot">${wa}<a class="rc-advisor__mail" href="mailto:?subject=${encodeURIComponent('Meine Klimaanlagen-Empfehlung')}&body=${encodeURIComponent(location.href)}" data-rc-track="advisor_result_email">${this.esc(s.mailResult)}</a></div>` +
      `</section>`;
    this.standing.hidden = true;
    this.track('advisor_result', { raum: this.state.raum, anzahl: this.state.anzahl, budget: this.state.budget, treffer: top.length });

    this.stage.querySelector('[data-restart]').addEventListener('click', () => {
      this.step = 1;
      this.render(false);
    });
    if (!fromHistory) this.persist(false);
    if (this.firstRenderDone) this.stage.querySelector('[data-question]').focus();
    this.firstRenderDone = true;
    this.animateIn();
  }
}

if (!customElements.get('rc-product-advisor')) {
  customElements.define('rc-product-advisor', RcProductAdvisor);
}
